import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

export interface UnderlineTabsTab<T extends string> {
  value: T;
  label: string;
  badge?: number | string;
}

interface UnderlineTabsProps<T extends string> {
  tabs: readonly UnderlineTabsTab<T>[];
  active: T;
  onChange: (value: T) => void;
  className?: string;
}

/**
 * Page-level underline tab bar — V2 standard.
 *
 * Use for the primary tabs of an admin page (List / Board / Archive, etc.).
 * For sub-tabs INSIDE a panel, use shadcn `<Tabs>` instead.
 *
 * Pair with `usePersistedTab(feature, validTabs, default)` from
 * `@shared/hooks/usePersistedFilters` for localStorage persistence.
 */
export function UnderlineTabs<T extends string>({ tabs, active, onChange, className }: UnderlineTabsProps<T>) {
  return (
    <div className={cn('flex items-center gap-0 border-b border-border', className)} role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.value}
          type="button"
          role="tab"
          aria-selected={active === tab.value}
          onClick={() => onChange(tab.value)}
          className={cn(
            'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors inline-flex items-center',
            active === tab.value
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border',
          )}
        >
          {tab.label}
          {tab.badge !== undefined && tab.badge !== 0 && tab.badge !== '' && (
            <Badge variant="secondary" className="ml-2 h-5">{tab.badge}</Badge>
          )}
        </button>
      ))}
    </div>
  );
}
